import { Link } from "react-router-dom";
import { CircleHelp } from "lucide-react";

import { usePoolPhaseRef, useReplicaPhaseRef } from "@/api/local/phases";
import type { Pool, PoolPhase, ReplicaPhase } from "@/api/types";
import { Pill, type PillTone } from "@/components/Pill";

interface PoolBadgeProps {
  phase: PoolPhase;
  reason?: string | null;
  /**
   * The Pool row the badge describes. When provided, the badge
   * renders the controller-derived honest presentation
   * (`status_chip` / `status_summary` / `attention_level`) instead
   * of the bare phase label — so a capacity-waiting pool shows
   * "Launching — capacity wait 47m" in amber, never a calm blue
   * "Launching". Callers without a full Pool row (reference pages)
   * omit it and get the legacy phase-only badge.
   */
  pool?: Pool;
  /**
   * Hides the inline ?-icon that links to `/reference/phases`.
   * Useful inside dense tables / tree rows where the icon would
   * clutter the layout — those callers already link to the
   * Reference page via the global nav.
   */
  hideHelpLink?: boolean;
  compact?: boolean;
}

/**
 * Tone override for the derived attention level. `ok` returns null:
 * the phase descriptor's own tone stays authoritative when nothing
 * is degraded.
 */
function attentionTone(pool: Pool | undefined): PillTone | null {
  if (!pool) return null;
  if (pool.attention_level === "action_required") return "danger";
  if (pool.attention_level === "self_healing") return "warn";
  return null;
}

/**
 * Pool-level phase badge — the canonical UI surface for
 * `Pool.phase`. Renders a tone-coded pill with a tooltip carrying
 * the operator-facing description, plus an inline ?-icon that
 * deep-links to the `/reference/phases#pool-<slug>` anchor for the
 * full explanation. The label / tone / description all come from
 * `GET /reference/phases` so adding a new phase on the controller
 * side propagates with no UI deploy beyond widening the type
 * union.
 */
export function PoolPhaseBadge({
  phase,
  reason,
  pool,
  hideHelpLink,
  compact,
}: PoolBadgeProps) {
  const ref = usePoolPhaseRef(phase);
  const phaseLabel = ref?.label ?? phase;
  // Honest presentation wins over the bare phase label whenever the
  // caller handed us the Pool row: the chip text carries the real
  // condition ("Launching — capacity wait 47m") and degraded levels force
  // an amber/red tone regardless of the phase's own optimistic tone.
  const label = pool?.status_chip ? pool.status_chip : phaseLabel;
  const tone = attentionTone(pool) ?? ref?.tone ?? "neutral";
  const tooltipLines = [
    pool?.status_summary ?? phaseLabel,
    ref?.description ?? "",
  ];
  if (reason) tooltipLines.push(`Reason: ${reason}`);
  const title = tooltipLines.filter(Boolean).join("\n");
  return (
    <span className="phase-badge" data-compact={compact ? "1" : undefined}>
      <Pill tone={tone} title={title}>
        {label}
      </Pill>
      {!hideHelpLink && (
        <Link
          to={`/reference/phases#pool-${phase}`}
          className="phase-badge__help"
          aria-label={`Open phase reference for ${label}`}
          title={`Open phase reference for ${label}`}
        >
          <CircleHelp size={12} />
        </Link>
      )}
    </span>
  );
}

interface ReplicaBadgeProps {
  phase: ReplicaPhase;
  hideHelpLink?: boolean;
  compact?: boolean;
}

/**
 * Replica-level twin of {@link PoolPhaseBadge}. Same hover / link
 * semantics; renders against {@link ReplicaPhase} instead.
 */
export function ReplicaPhaseBadge({
  phase,
  hideHelpLink,
  compact,
}: ReplicaBadgeProps) {
  const ref = useReplicaPhaseRef(phase);
  const label = ref?.label ?? phase;
  const tone = ref?.tone ?? "neutral";
  const tooltipLines = [
    `${label}${ref?.is_terminal ? " (terminal)" : ""}`,
    ref?.description ?? "",
  ];
  const title = tooltipLines.filter(Boolean).join("\n");
  return (
    <span className="phase-badge" data-compact={compact ? "1" : undefined}>
      <Pill tone={tone} title={title}>
        {label}
      </Pill>
      {!hideHelpLink && (
        <Link
          to={`/reference/phases#replica-${phase}`}
          className="phase-badge__help"
          aria-label={`Open phase reference for ${label}`}
          title={`Open phase reference for ${label}`}
        >
          <CircleHelp size={12} />
        </Link>
      )}
    </span>
  );
}
